
import React, { useState, useEffect, useCallback, ErrorInfo, ReactNode, Component, useMemo, useRef } from 'react';
import { Login, LoginProps } from '@/components/Login';
import { Layout } from '@/components/Layout';
import { ChatInterface } from '@/components/ChatInterface';
import { ArticleSubmission } from '@/components/ArticleSubmission';
import { AdminPanel, AdminPanelProps } from '@/components/AdminPanel';
import { Dashboard } from '@/components/Dashboard';
import { MyArticles } from '@/components/MyArticles';
import { ArticleView } from '@/components/ArticleView';
// Import CreateSectorModalProps type from the new interfaces file
import { CreateSectorModal } from '@/components/CreateSectorModal';
import { CreateSectorModalProps } from '@/components/interfaces'; // Importado da interface centralizada
import { User, Article, ViewState, ArticleStatus, Sector, UserRole, AppSettings, MaintenanceSettings } from '@/types'; // CORRECTED IMPORT PATH
import { 
  subscribeToArticles, 
  subscribeToUsers, 
  subscribeToSectors,
  saveArticle,
  updateArticleStatus,
  deleteArticle,
  saveUser,
  deleteUser,
  saveSector,
  deleteSector,
  // Replaced getAppSettings with subscribeToAppSettings
  subscribeToAppSettings, 
  saveAppSettings,
  subscribeToMaintenanceSettings,
  saveMaintenanceSettings,
  checkOldChatSessionsExist, // NEW: Import checkOldChatSessionsExist
  migrateChatSessions // NEW: Import migrateChatSessions
} from '@/services/database';
import { supabase } from '@/supabaseClient';
import { AlertTriangle, Settings, Loader2, CreditCard, ExternalLink, Wrench } from 'lucide-react';
import { DEFAULT_CONFIG } from '@/config';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';

const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

class ErrorBoundary extends Component<{children?: ReactNode}, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, error: null };
  public static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  public render() {
    if (this.state.hasError) {
      const errorObj = this.state.error as any;
      const isQuotaError = errorObj?.code === 'resource-exhausted' || errorObj?.message?.includes('quota');
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="bg-white p-10 rounded-3xl shadow-2xl max-w-lg w-full border border-slate-100 text-center">
            {isQuotaError ? (
              <>
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 text-orange-600"><CreditCard className="w-10 h-10" /></div>
                <h1 className="text-2xl font-black text-slate-900 mb-4">Limite de Banco de Dados</h1>
                <p className="text-slate-600 mb-8 text-sm leading-relaxed">O plano gratuito do Supabase atingiu o limite. Verifique as configurações de faturamento.</p>
                <div className="space-y-3">
                  <a href="https://supabase.com/dashboard/project/bvuvtlesliujcdjysfwp/settings/billing" target="_blank" className="w-full py-4 bg-orange-600 text-white rounded-2xl hover:bg-orange-700 transition-all font-bold flex items-center justify-center gap-2">Verificar Faturamento Supabase <ExternalLink className="w-4 h-4" /></a>
                  <button onClick={() => window.location.reload()} className="w-full py-3 text-slate-400 font-medium hover:text-slate-600">Recarregar página</button>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 text-red-600"><AlertTriangle className="w-10 h-10" /></div>
                <h1 className="text-2xl font-black text-slate-900 mb-4">Erro Crítico</h1>
                <p className="text-slate-600 mb-8 text-sm">{errorObj?.message || "Erro desconhecido"}</p>
                <button onClick={() => window.location.reload()} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold">Tentar Novamente</button>
              </>
            )}
          </div>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

const AppContent: React.FC = () => {
  const env = (import.meta as any).env;
  const isSupabaseInitialized = !!(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY);

  if (!isSupabaseInitialized) return <div className="p-8">Supabase não inicializado. Verifique as variáveis VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no arquivo .env.local.</div>;

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [currentView, setCurrentView] = useState<ViewState>('dashboard');
  const [articleToEdit, setArticleToEdit] = useState<Article | null>(null);
  const [viewingArticle, setViewingArticle] = useState<Article | null>(null);
  
  const [articles, setArticles] = useState<Article[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]); // CORRECTED: Removed extra parenthesis
  const [isForcePasswordChangeOpen, setIsForcePasswordChangeOpen] = useState(false);
  
  const [appSettings, setAppSettings] = useState<AppSettings>(DEFAULT_CONFIG); // Initialize with default config
  const [dashboardSelectedSector, setDashboardSelectedSector] = useState<Sector | null>(null);

  // --- Maintenance Mode State ---
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string>("A Wiki da SÓES está temporariamente em manutenção para melhorias. Volte em breve!");
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(true);

  // New loading state for app settings
  const [isLoadingAppSettings, setIsLoadingAppSettings] = useState(true);

  // Easter Egg State
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  // --- States for CreateSectorModal ---
  const [isCreateSectorModalOpen, setIsCreateSectorModalOpen] = useState(false);
  const [initialParentIdForNewSector, setInitialParentIdForNewSector] = useState<string | null>(null);
  const [editingSectorForModal, setEditingSectorForModal] = useState<Sector | null>(null); // To pass sector data for editing

  // Callback to open the CreateSectorModal
  const handleOpenCreateSectorModal = (parentId: string | null = null, sectorToEdit: Sector | null = null) => {
    setInitialParentIdForNewSector(parentId);
    setEditingSectorForModal(sectorToEdit);
    setIsCreateSectorModalOpen(true);
  };

  // Memoize initialSectorData for stability
  const memoizedInitialSectorData: CreateSectorModalProps['initialSectorData'] = useMemo(() => {
    if (editingSectorForModal) {
      return { id: editingSectorForModal.id, name: editingSectorForModal.name, parentId: editingSectorForModal.parentId };
    }
    // For a new sector, always return an object with id/name as undefined and the specified parentId.
    // This ensures initialSectorData is consistently an object when the modal is open.
    return { id: undefined, name: undefined, parentId: initialParentIdForNewSector };
  }, [editingSectorForModal, initialParentIdForNewSector]);

  // Subscribe to Maintenance Settings
  useEffect(() => {
    const unsubscribe = subscribeToMaintenanceSettings((settings) => {
      console.log("App.tsx: Listener de Maintenance Settings disparado. Novas configurações recebidas do DB:", settings);
      setIsMaintenanceMode(settings.isMaintenanceMode);
      setMaintenanceMessage(settings.message);
      setIsLoadingMaintenance(false);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to App Settings (real-time listener)
  useEffect(() => {
    const env = (import.meta as any).env;
    const isSupabaseInitialized = !!(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY);
    if (!isSupabaseInitialized) return;

    setIsLoadingAppSettings(true); // Indicate loading when subscription starts
    const unsubscribe = subscribeToAppSettings((settingsFromDb) => {
      console.log("App.tsx: Listener de App Settings disparado. Novas configurações recebidas do DB:", settingsFromDb);
      setAppSettings(prev => {
        const rawAppName = settingsFromDb.appName || DEFAULT_CONFIG.appName || 'SÓES Wiki';
        const sanitizedAppName = rawAppName.replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES') || 'SÓES Wiki';
        const newSettings = { 
          ...DEFAULT_CONFIG, // Always start with defaults
          ...settingsFromDb,  // Override with settings from DB (empty if doc not found)
          appName: sanitizedAppName,
          lastUpdatedAt: settingsFromDb.lastUpdatedAt || Date.now() // Ensure lastUpdatedAt is always set
        };
        console.log("App.tsx: App Settings state updated to:", newSettings);
        return newSettings;
      });
      setIsLoadingAppSettings(false); // Done loading when first snapshot arrives
    });

    return () => unsubscribe(); // Unsubscribe on component unmount
  }, [currentUser]);

  // Sincroniza o novo nome no banco de dados se o admin estiver logado e o banco ainda tiver o nome antigo
  useEffect(() => {
    if (currentUser?.role === UserRole.ADMIN && appSettings.appName) {
      if (/s[óo]\s+escrit[óo]rio/i.test(appSettings.appName)) {
        const updated = {
          ...appSettings,
          appName: appSettings.appName.replace(/s[óo]\s+escrit[óo]rio/gi, 'SÓES'),
          lastUpdatedAt: Date.now()
        };
        saveAppSettings(updated).catch(err => console.warn("Erro ao atualizar appName no banco:", err));
      }
    }
  }, [currentUser, appSettings.appName]);

  // Easter Egg Listener
  useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
          // Ativa o Easter Egg com Alt + Q
          if (event.altKey && event.code === 'KeyQ') {
              event.preventDefault(); // Prevent default browser action if any
              setShowEasterEgg(true);
              
              setTimeout(() => {
                  setShowEasterEgg(false);
              }, 5000); // Hide after 5 seconds
          }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
          window.removeEventListener('keydown', handleKeyDown);
      };
  }, []);


  const handleLogout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setCurrentUser(null);
      setCurrentView('login');
      window.location.reload();
    } catch (error) {
      console.error("Erro ao deslogar:", error);
    }
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (error) throw error;
          if (data) {
            const userData: User = {
              id: data.id,
              fullName: data.full_name,
              username: data.username,
              email: data.email,
              sector: data.sector,
              role: data.role,
              avatar: data.avatar || undefined,
              mustChangePassword: data.must_change_password
            };
            setCurrentUser(userData);
            if (userData.mustChangePassword) setIsForcePasswordChangeOpen(true);
          }
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Session restore error:", error);
      } finally {
        setIsAuthChecking(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Supabase auth state changed:", event, session?.user?.email);
      if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setIsAuthChecking(false);
      } else if (session?.user) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (error) throw error;
          if (data) {
            const userData: User = {
              id: data.id,
              fullName: data.full_name,
              username: data.username,
              email: data.email,
              sector: data.sector,
              role: data.role,
              avatar: data.avatar || undefined,
              mustChangePassword: data.must_change_password
            };
            setCurrentUser(userData);
            if (userData.mustChangePassword) setIsForcePasswordChangeOpen(true);
          }
        } catch (error) {
          console.error("Auth state change user fetch error:", error);
        } finally {
          setIsAuthChecking(false);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      const unsubA = subscribeToArticles(setArticles);
      const unsubU = subscribeToUsers(setUsers);
      const unsubS = subscribeToSectors(setSectors);
      return () => { unsubA(); unsubU(); unsubS(); };
    }
  }, [currentUser]);

  const handleViewArticle = (article: Article) => {
    setViewingArticle(article);
    setCurrentView('article-view');
  };

  const handleDeleteArticleAction = async (id: string) => {
    // Segurança adicionada para exclusão de artigos
    if (window.confirm("❗ ATENÇÃO: Tem certeza que deseja excluir este artigo?\n\nEsta ação é irreversível e removerá o conteúdo e seus anexos permanentemente.")) {
        try {
            await deleteArticle(id);
            alert("Artigo excluído com sucesso!");
        } catch (error: any) {
            alert("Erro ao excluir artigo: " + error.message);
        }
    }
  };

  const handleDeleteSectorAction = async (id: string) => {
    try {
        await deleteSector(id);
        alert("Setor excluído com sucesso!");
    } catch (error: any) {
        alert("Erro ao excluir setor: " + error.message);
    }
  };

  // Os outros handlers permanecem iguais pois a confirmação de usuário já é feita no AdminPanel
  const handleDeleteUserAction = async (id: string) => {
    try {
        await deleteUser(id);
        alert("Usuário removido com sucesso!");
    } catch (error: any) {
        alert("Erro ao remover usuário: " + error.message);
    }
  };

  const handleUpdateArticleStatusAction = async (id: string, status: ArticleStatus) => {
    try {
        await updateArticleStatus(id, status);
        alert(`Status do artigo atualizado para "${status}" com sucesso!`);
    } catch (error: any) {
        alert("Erro ao atualizar status do artigo: " + error.message);
    }
  };

  // Session Timeout Logic (continua igual)
  const [lastActivity, setLastActivity] = useState(Date.now());
  const alertShownRef = useRef(false);

  useEffect(() => {
    if (!currentUser) {
      alertShownRef.current = false; // Reset ref when user logs out
      return;
    }

    const handleActivity = () => {
      setLastActivity(Date.now());
    };

    // REMOVIDO: 'keydown' para evitar re-renderizações excessivas em inputs
    const events = ['mousedown', 'scroll', 'touchstart']; 
    events.forEach(event => window.addEventListener(event, handleActivity));

    const interval = setInterval(() => {
      if (Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
        if (!alertShownRef.current) {
          alertShownRef.current = true;
          handleLogout();
          alert('Sua sessão expirou por inatividade. Por favor, faça login novamente.');
        }
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearInterval(interval);
    };
  }, [currentUser, lastActivity, handleLogout]);


  const handleLogin = (user: User) => {
    setCurrentUser(user);
    setCurrentView('dashboard');
    setLastActivity(Date.now());
  };

  const handleNavigate = (view: ViewState) => {
    if (view === 'submit') { // Se a view for 'submit', sempre limpe 'articleToEdit'
      setArticleToEdit(null);
    }
    if (view === 'dashboard') {
      setDashboardSelectedSector(null);
    }
    setCurrentView(view);
  };

  // --- START Conditional Renders for main UI ---
  if (isAuthChecking || isLoadingMaintenance || isLoadingAppSettings) {
    // Add detailed logs here to see which state is true
    console.log("App.tsx: Loading screen active.");
    console.log(`  isAuthChecking: ${isAuthChecking}`);
    console.log(`  isLoadingMaintenance: ${isLoadingMaintenance}`);
    console.log(`  isLoadingAppSettings: ${isLoadingAppSettings}`);

    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" />
        {/* Easter Egg Overlay - Renderizado SEMPRE, mesmo durante o carregamento */}
        <div 
          id="easter-egg-overlay" // Adicionado ID para fácil localização
          className="fixed inset-0 z-[101] flex items-center justify-center pointer-events-none animate-fade-in"
          style={{ display: showEasterEgg ? 'flex' : 'none' }} // Controla a visibilidade
        >
          <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl border border-blue-600 text-lg font-bold">
            O App SÓES Wiki foi criado por André Luiz Pinheiro Silva.
          </div>
        </div>
      </div>
    );
  }
  
  if (!currentUser) {
    return (
      <>
        <Login
          onLogin={handleLogin} 
          appLogo={appSettings.logoUrl} 
          backgroundImage={appSettings.loginBackgroundImageUrl} 
          isMaintenanceMode={isMaintenanceMode}
          maintenanceMessage={maintenanceMessage}
          appName={appSettings.appName}
          settingsUpdatedAt={appSettings.lastUpdatedAt} // Passando settingsUpdatedAt para Login
        />
        {/* Easter Egg Overlay - Renderizado SEMPRE, mesmo na tela de login */}
        <div 
          id="easter-egg-overlay" // Adicionado ID para fácil localização
          className="fixed inset-0 z-[101] flex items-center justify-center pointer-events-none animate-fade-in"
          style={{ display: showEasterEgg ? 'flex' : 'none' }} // Controla a visibilidade
        >
          <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl border border-blue-600 text-lg font-bold">
            O App SÓES Wiki foi criado por André Luiz Pinheiro Silva.
          </div>
        </div>
      </>
    );
  }
  // --- END Conditional Renders for main UI ---

  const isAdmin = currentUser.role === UserRole.ADMIN;

  return (
    <>
      <Layout 
        currentUser={currentUser} 
        currentView={currentView} 
        onNavigate={handleNavigate} 
        onLogout={handleLogout} 
        appLogo={appSettings.logoUrl}
        appName={appSettings.appName} // Pass appName to Layout
        pendingApprovalCount={articles.filter(a => a.status === ArticleStatus.PENDING).length}
        settingsUpdatedAt={appSettings.lastUpdatedAt} // Passando settingsUpdatedAt para Layout
      >
        {currentView === 'dashboard' && <Dashboard currentUser={currentUser} articles={articles} sectors={sectors} onNavigate={handleNavigate} onViewArticle={handleViewArticle} selectedSector={dashboardSelectedSector} onSelectSector={setDashboardSelectedSector} onOpenCreateSectorModal={handleOpenCreateSectorModal} />}
        {currentView === 'chat' && <ChatInterface currentUser={currentUser} articles={articles} />}
        {currentView === 'article-view' && viewingArticle && 
          <ArticleView 
            article={viewingArticle} 
            onBack={() => setCurrentView('dashboard')} 
            onNavigateHome={() => {setDashboardSelectedSector(null); setCurrentView('dashboard');}} 
            onNavigateSector={(sector: Sector | null) => { /* Fix: Pass the sector object, not just ID */
              setDashboardSelectedSector(sector); 
              setCurrentView('dashboard');
            }} 
          />
        }
        {/* Fix: Add the `onSaveSector` prop as required by ArticleSubmissionProps. */}
        {currentView === 'submit' && <ArticleSubmission currentUser={currentUser} sectors={sectors} onSubmit={async (a: Article, f: File[]) => { await saveArticle(a, f); setCurrentView('my-articles'); }} onSaveSector={saveSector} initialData={articleToEdit} />}
        {currentView === 'my-articles' && <MyArticles currentUser={currentUser} articles={articles} onEdit={(a: Article) => {setArticleToEdit(a); setCurrentView('submit');}} />}
        {(currentView === 'admin' || currentView === 'approvals' || currentView === 'migration') && isAdmin && (
          <AdminPanel 
            users={users} 
            sectors={sectors} 
            articles={articles} 
            onUpdateArticleStatus={handleUpdateArticleStatusAction} 
            onEditArticle={(a: Article) => {setArticleToEdit(a); setCurrentView('submit');}} 
            onDeleteArticle={handleDeleteArticleAction} 
            onViewArticle={handleViewArticle} 
            onSaveUser={saveUser} 
            onDeleteUser={deleteUser} 
            onSaveSector={saveSector} 
            onDeleteSector={handleDeleteSectorAction} 
            onOpenCreateSectorModal={handleOpenCreateSectorModal} // Pass callback to AdminPanel
            currentSettings={appSettings} 
            onSaveSettings={async (s) => { await saveAppSettings(s); setAppSettings({ ...s, lastUpdatedAt: Date.now() }); }} // Update AppSettings state with new timestamp
            currentMaintenanceSettings={{isMaintenanceMode, message: maintenanceMessage}}
            onSaveMaintenanceSettings={saveMaintenanceSettings} 
            viewMode={currentView === 'approvals' ? 'approvals_only' : currentView === 'migration' ? 'migration_only' : 'full'} 
            initialTab={currentView === 'admin' ? 'articles' : currentView === 'migration' ? 'migration' : 'approvals'} 
            currentUser={currentUser} // Pass currentUser to AdminPanel for ChatHistoryReport
          />
        )}
      </Layout>
      {isForcePasswordChangeOpen && <ChangePasswordModal onClose={() => setIsForcePasswordChangeOpen(false)} forceChange={true} />}

      {/* Easter Egg Overlay - Renderizado SEMPRE (movido para fora dos blocos condicionais) */}
      <div 
        id="easter-egg-overlay" 
        className="fixed inset-0 z-[101] flex items-center justify-center pointer-events-none animate-fade-in"
        style={{ display: showEasterEgg ? 'flex' : 'none' }}
      >
        <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl border border-blue-600 text-lg font-bold">
          O App SÓES Wiki foi criado por André Luiz Pinheiro Silva.
        </div>
      </div>

      {isCreateSectorModalOpen && (
        <CreateSectorModal
          key={editingSectorForModal ? `edit-${editingSectorForModal.id}` : `new-${initialParentIdForNewSector || 'top-level'}`} // ADDED KEY PROP
          sectors={sectors}
          onClose={() => setIsCreateSectorModalOpen(false)}
          onSaveSector={saveSector}
          currentUser={currentUser}
          initialSectorData={memoizedInitialSectorData} // USANDO A PROPS MEMOIZADA AQUI
        />
      )}
    </>
  );
};

// Fix: Converted to a named function and then explicitly exported as default.
// This often resolves "Module has no default export" issues in some bundlers.
export function App() { return <ErrorBoundary><AppContent /></ErrorBoundary>; }
export default App;